import { Form as ReactForm } from 'react-bootstrap';

import React, { useEffect, useState } from 'react';
import { SalvBanner, SalvBannerType, SalvBannerAccent, SalvButton, SalvModal, Size } from '@salvapp/ui-components';
import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import './ConversationConclusionModal.scss';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  BridgeApiConversation,
  BridgeApiConversationConclusion,
  BridgeApiTemplateField,
  BridgeApiTemplateFieldTypeEnum,
} from '../../api-internal';
import { conversationConclusionApi } from '../../apis';
import { constructValidationSchema, getField, getMessengerFieldConfiguration } from '../helper';
import { faTrashXmark } from '@fortawesome/pro-light-svg-icons';
import { sorted } from '../common/helper';

export interface ConversationConclusionModalProps {
  showModal: boolean;
  isEditing: boolean;
  conversation: BridgeApiConversation;
  conclusionFields: BridgeApiTemplateField[];
  providedAttributes?: { [key: string]: any };
  onClose: () => void;
}

export interface ConversationConclusionFormFormValues {
  [key: string]: string | number | boolean | null | Array<string> | Array<number>;
}

const displayField = (field: BridgeApiTemplateField, value: any, setFieldValue: any, errors: any) => {
  const fieldConf = getMessengerFieldConfiguration(field);

  const hasLabel = !fieldConf.repeatable;

  return (
    <ReactForm.Group key={field.name} className='form-group'>
      {hasLabel ? (
        <>
          <label className='form-label' htmlFor={field.name}>
            {field.displayName}
          </label>
          {field.required && <span className='required-asterisk'>*</span>}
        </>
      ) : (
        <></>
      )}
      <div className='conclusion-field'>
        {getField(field, value, errors[field.name], (val: any) => setFieldValue(field.name, val), Size.MEDIUM)}
      </div>
    </ReactForm.Group>
  );
};

export const getEmptyFieldValue = (field: BridgeApiTemplateField) => {
  const repeatable = getMessengerFieldConfiguration(field).repeatable || false;
  if (repeatable) return [field.type === BridgeApiTemplateFieldTypeEnum.BOOLEAN ? null : ''];

  if (field.type === BridgeApiTemplateFieldTypeEnum.BOOLEAN) return null;

  return '';
};

const getExistingFields = (conclusion?: BridgeApiConversationConclusion) => {
  try {
    return JSON.parse(conclusion?.content || '{}');
  } catch (_) {
    return {};
  }
};

export const ConversationConclusionModal = ({
  showModal,
  isEditing,
  conversation,
  conclusionFields,
  providedAttributes = {},
  onClose,
}: ConversationConclusionModalProps) => {
  const [conclusion, setConclusion] = useState<BridgeApiConversationConclusion>();

  useEffect(() => {
    conversationConclusionApi
      .findConversationConclusionByConversationId({
        conversationId: conversation.id,
      })
      .then((conclusion: BridgeApiConversationConclusion) => {
        setConclusion(conclusion);
      })
      .catch(() => {
        // ignore - no conclusion found
      });
  }, []);

  const existingFields = getExistingFields(conclusion);

  const getInitialValue = (field: BridgeApiTemplateField) => {
    if (existingFields[field.name] !== undefined) return existingFields[field.name];

    if (providedAttributes[field.name] !== undefined) return providedAttributes[field.name];

    return getEmptyFieldValue(field);
  };

  const initialValues = conclusionFields.reduce((acc, field) => {
    return {
      ...acc,
      [field.name]: getInitialValue(field),
    };
  }, {});

  const filterValuesByShowWhen = (values: ConversationConclusionFormFormValues) => {
    return Object.entries(values)
      .filter(([key]) => {
        const field = conclusionFields.find((f: BridgeApiTemplateField) => f.name === key);

        const showWhenConf = field ? getMessengerFieldConfiguration(field).show_when : null;

        if (showWhenConf) {
          return values[showWhenConf.field_id] === showWhenConf.field_value;
        } else {
          return true;
        }
      })
      .reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value,
        }),
        {}
      );
  };
  const sendConversationConclusion = async (
    values: ConversationConclusionFormFormValues,
    { setSubmitting }: FormikHelpers<ConversationConclusionFormFormValues>
  ) => {
    setSubmitting(true);

    let newConversationConclusion = null;
    const conclusionValues = filterValuesByShowWhen(values);

    if (conclusion) {
      const newContent = JSON.stringify(conclusionValues);

      if (conclusion.content !== newContent) {
        newConversationConclusion = await conversationConclusionApi.updateConversationConclusion({
          id: conclusion.id,
          bridgeApiUpdateConversationConclusion: {
            content: newContent,
          },
        });
      }
    } else {
      newConversationConclusion = await conversationConclusionApi.addConversationConclusion({
        conversationId: conversation.id,
        bridgeApiCreateConversationConclusion: {
          content: JSON.stringify(conclusionValues),
          templateId: conversation.templateId,
        },
      });
    }

    if (newConversationConclusion) {
      setConclusion(newConversationConclusion);
    }

    onClose();
    setSubmitting(false);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={constructValidationSchema(
        conclusionFields,
        conclusionFields.map((f) => f.name)
      )}
      onSubmit={sendConversationConclusion}
    >
      {({ values, setFieldValue, isSubmitting, errors }: FormikProps<ConversationConclusionFormFormValues>) => {
        return (
          <SalvModal show={showModal} onHide={onClose} className='conclusion-modal' allowDismissal={false}>
            <Form>
              <SalvModal.Header>Case Conclusion</SalvModal.Header>
              <SalvModal.Body>
                {sorted(conclusionFields).map((field) => {
                  const configuration = getMessengerFieldConfiguration(field);
                  const showWhenConf = configuration.show_when;
                  const isHidden = showWhenConf ? values[showWhenConf.field_id] !== showWhenConf.field_value : false;

                  if (isHidden) {
                    return null;
                  }

                  if (configuration.repeatable) {
                    return (
                      <div key={field.name}>
                        <label>{field.displayName}</label>
                        {field.required && <span className='required-asterisk'>*</span>}
                        <FieldArray name={field.name}>
                          {({ remove, push, replace, form: { values, errors } }: FieldArrayRenderProps) => {
                            return (
                              <div>
                                {values[field.name].map((value: any, index: number) => (
                                  <div className='subfield-row' key={field.name + index}>
                                    {displayField(
                                      {
                                        ...field,
                                        name: field.name + index,
                                      },
                                      value,
                                      (fieldName: string, val: any) => replace(index, val),
                                      errors
                                    )}

                                    {index !== 0 && (
                                      <FontAwesomeIcon
                                        icon={faTrashXmark}
                                        onClick={() => remove(index)}
                                        color='var(--danger-default)'
                                      />
                                    )}
                                  </div>
                                ))}

                                <SalvButton
                                  className='m-b-16'
                                  variant='secondary'
                                  size={Size.SMALL}
                                  onClick={() => {
                                    const emptyValue = getEmptyFieldValue(field);
                                    push(emptyValue);
                                  }}
                                  label='New field'
                                  icon={faPlus}
                                />
                              </div>
                            );
                          }}
                        </FieldArray>
                      </div>
                    );
                  }

                  return (
                    <div key={field.name}>
                      {displayField(
                        field,
                        values[field.name],
                        (fieldName: string, value: any) => {
                          // if dependentField disappears, also erase ts previous value from formik
                          const dependentField = conclusionFields.find(
                            (f: BridgeApiTemplateField) =>
                              getMessengerFieldConfiguration(f).show_when?.field_id == fieldName
                          );

                          if (dependentField) {
                            setFieldValue(dependentField.name, getEmptyFieldValue(dependentField));
                          }
                          setFieldValue(fieldName, value);
                        },
                        errors
                      )}
                    </div>
                  );
                })}

                {conclusion && !isEditing && (
                  <SalvBanner
                    type={SalvBannerType.WARNING}
                    accent={SalvBannerAccent.LIGHT}
                    message='Please review case conclusion data fields. Are they still correct?'
                    className='m-b-16'
                  />
                )}
                <SalvBanner
                  type={SalvBannerType.INFO}
                  accent={SalvBannerAccent.LIGHT}
                  message='This information will be available to all investigation participants'
                />
              </SalvModal.Body>

              <SalvModal.Footer>
                <SalvButton
                  variant='muted'
                  disabled={false}
                  size={Size.MEDIUM}
                  onClick={onClose}
                  label='Fill in later'
                />

                <SalvButton type='submit' variant='primary' disabled={isSubmitting} size={Size.MEDIUM} label='Submit' />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};
